import { effect, inject, untracked } from '@angular/core';
import { Logger } from '@df/core/service/logger';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import isEqual from 'lodash-es/isEqual';
import { pipe, switchMap, tap } from 'rxjs';
import { AdvancedCommerce } from '../../advanced-commerce.namespace';
import { AdvancedCommerceService } from '../../advanced-commerce.service';
import { AdvancedCommerceStore } from '../advanced-commerce-store.namespace';
import { AdvancedCommerceStoreService } from '../advanced-commerce-store.service';
import { withItems } from './items.feature';
import { withMeta } from './meta.feature';

export function withResponse() {
    return signalStoreFeature(
        {
            // to use `store.data()` we need provide state from base feature
            state: type<AdvancedCommerceStore.State>(),
            methods: type<{
                loadConfig(): Promise<AdvancedCommerceStore.Config.Config>;
            }>()
        },
        withState<AdvancedCommerceStore.Response.State>({
            isLoading: false,
            isError: false,
            response: undefined,
            redirect: undefined
        }),
        withMeta(),
        withItems(),
        withMethods(store => {
            const advancedCommerceService = inject(AdvancedCommerceService);
            const logger = inject(Logger);

            return {
                responseSet(response?: AdvancedCommerce.Query.Response) {
                    patchState(store, { response: response });
                },
                responseLoadQueryByData: rxMethod<Partial<AdvancedCommerce.Query.RequestData>>(
                    pipe(
                        tap(() => patchState(store, { isLoading: true })),
                        switchMap(data =>
                            data.elements?.length || data.template
                                ? advancedCommerceService.query<AdvancedCommerce.Query.Response>(data).pipe(
                                      tapResponse({
                                          next: response => {
                                              if (response?.error) {
                                                  patchState(store, { isError: true });
                                                  logger.error('Error loading items', response.error);
                                                  return;
                                              }

                                              patchState(store, { response });
                                          },
                                          error: error => {
                                              patchState(store, { isError: true });
                                              logger.error('Error loading items', error);
                                          },
                                          finalize: () => patchState(store, { isLoading: false })
                                      })
                                  )
                                : []
                        )
                    )
                ),
                setRedirect(redirect: AdvancedCommerceStore.Response.Redirect | undefined) {
                    patchState(store, { redirect });
                }
            };
        }),
        withHooks(store => {
            const advancedCommerceStoreService = inject(AdvancedCommerceStoreService);

            let lastData = store.data();

            return {
                onInit() {
                    store.loadConfig();

                    /**
                     * Load query on data update
                     */
                    effect(() => {
                        const data = store.data();

                        untracked(() => {
                            if (!isEqual(lastData, data)) {
                                lastData = data;

                                store.loadConfig().then(() => {
                                    store.responseLoadQueryByData(data);
                                });
                            }
                        });
                    });

                    /**
                     * Update redirect on request load
                     */
                    effect(() => {
                        const response = store.response();
                        const element = store.element();

                        if (response && element) {
                            untracked(() => {
                                const data = store.data();
                                const currentRedirect = store.redirect();

                                const redirect = advancedCommerceStoreService.getRedirect(
                                    'products',
                                    response,
                                    element,
                                    data.context?.search_text
                                );

                                if (redirect !== currentRedirect) {
                                    store.setRedirect(redirect);
                                }
                            });
                        }
                    });
                }
            };
        })
    );
}
